import { ClientChildType, MIVClient, WebSocketConnection, LoadFilter, MIVConstants, ClientAbstractChildType, ErrorMessage } from "@1oo4/miv";
import { MArticle, MCell, MCellContent, MCellPicture, MCellText, MChapter, MCode, MCodeTab, MColumn, MContent, MPicture, MRow, MSection, MTable, MText } from "./model/mdoc";
import { MDocSystem, MDocUser, MRootDoc } from "./model/masterdata";
import { DocIDS } from "./ids";
import { MRichTextItem, RichTextItem, RichTextParser } from "./model/richtext";
import { updateSignInStatus } from "..";

export class DocClient extends MIVClient {

    public readonly DocSystem: MDocSystem;
    public readonly DocRoot: MRootDoc;

    /** @internal */ readonly users: ClientChildType<MDocUser>
    /** @internal */ readonly chapters: ClientChildType<MChapter>;
    /** @internal */ readonly articles: ClientChildType<MArticle>;
    /** @internal */ readonly sections: ClientChildType<MSection>;
    /** @internal */ readonly contents: ClientAbstractChildType<MContent>;
    /** @internal */ readonly texts: ClientChildType<MText>;
    /** @internal */ readonly pictures: ClientChildType<MPicture>;
    /** @internal */ readonly codes: ClientChildType<MCode>;
    /** @internal */ readonly codeTabs: ClientChildType<MCodeTab>;
    /** @internal */ readonly tables: ClientChildType<MTable>;
    /** @internal */ readonly columns: ClientChildType<MColumn>;
    /** @internal */ readonly cells: ClientChildType<MCell>;
    /** @internal */ readonly cellContent: ClientAbstractChildType<MCellContent>;
    /** @internal */ readonly cellTexts: ClientChildType<MCellText>;
    /** @internal */ readonly cellPictures: ClientChildType<MCellPicture>;

    /** @internal */ readonly fullLoad: LoadFilter;
    /** @internal */ readonly docLoad: LoadFilter;

    //#region  SHARED TOOLS

    /** @internal */ readonly cellsList: Array<MCell> = new Array();
    /** @internal */ readonly rowsList: Array<MRow> = new Array();
    /** @internal */ readonly richMListTool: Array<MRichTextItem> = new Array();
    /** @internal */ readonly richParser: RichTextParser = new RichTextParser();

    //#endregion

    private constructor() {
        //super(new WebSocketConnection("ws://" + location.hostname + ":3003/ws/"))
        super(new WebSocketConnection("wss://" + location.hostname + "/ws/"))

        this.DefaultQueryHistory = false;

        this.users = this.RegisterChildType(MIVConstants.TYPE_USER, x => new MDocUser(x), "user", this.GenericParentChildType, false);
        this.chapters = this.RegisterChildType(DocIDS.TY_CHAPTER, x => new MChapter(x, this), "chapter", this.GenericParentChildType, false);
        this.articles = this.RegisterChildType(DocIDS.TY_ARTICLE, x => new MArticle(x, this), "article", this.GenericParentChildType, false);
        this.sections = this.RegisterChildType(DocIDS.TY_SECTION, x => new MSection(x, this), "article", this.GenericParentChildType, false);
        this.contents = new ClientAbstractChildType(this, "content", this.GenericParentChildType, false);
        this.texts = this.RegisterChildType(DocIDS.TY_TEXT, x => new MText(x), "text", this.contents, false);
        this.pictures = this.RegisterChildType(DocIDS.TY_PICTURE, x => new MPicture(x), "picture", this.contents, false);
        this.codes = this.RegisterChildType(DocIDS.TY_CODE, x => new MCode(x), "code", this.contents, false);
        this.codeTabs = this.RegisterChildType(DocIDS.TY_CODE_TAB, x => new MCodeTab(x), "code tab", this.GenericParentChildType, false);
        this.tables = this.RegisterChildType(DocIDS.TY_TABLE, x => new MTable(x), "table", this.contents, false);
        this.columns = this.RegisterChildType(DocIDS.TY_TABLE_COLUMN, x => new MColumn(x), "column", this.GenericParentChildType, false);
        this.cells = this.RegisterChildType(DocIDS.TY_TABLE_CELL, x => new MCell(x), "cell", this.GenericParentChildType, false);
        this.cellContent = new ClientAbstractChildType(this, "cell content", this.GenericParentChildType, false);
        this.cellTexts = this.RegisterChildType(DocIDS.TY_TABLE_CELL_TEXT, x => new MCellText(x), "cell text", this.cellContent, false);
        this.cellPictures = this.RegisterChildType(DocIDS.TY_TABLE_CELL_PICTURE, x => new MCellPicture(x), "cell picture", this.cellContent, false);

        this.fullLoad = LoadFilter.CreateNoFilter(1, -1);
        this.docLoad = LoadFilter.CreateNoFilter(2, 3);
        this.articles.SetupAvailableFilters(this.fullLoad);
        this.sections.SetupAvailableFilters(this.fullLoad);
        this.DocSystem = new MDocSystem(this);
        this.DocRoot = new MRootDoc(this);
    }

    /** @internal */
    static async getClientAsync(): Promise<DocClient | ErrorMessage> {
        if (window.client === undefined) {
            const client = new DocClient();
            const error = await client.StartAsync();
            //const error = await client.StartAsync();
            if (error === undefined) {
                console.log("client started");
                window.client = client;
                updateSignInStatus();
            } else {
                console.log("client start error");
                console.log(error);
                client.Stop();
                return error;
            }
        }
        return window.client;
    }

    protected ConnectionLost(): number {
        console.log("connection lost, trying to reconnect...");
        return 1004;
    }
    protected ReconnectAttempt(attempt: number, calledFromEnsure: boolean): void {
        console.log("attempting to reconnect (" + attempt + "/" + 5 + ") " + (calledFromEnsure ? "[following a ping]" : "") + "...");
    }
    protected ReconnectAttemptFailed(attempt: number, calledFromEnsure: boolean, error: ErrorMessage): number {
        console.log("reconnect attempt " + attempt + " FAILED");
        console.log(error);
        if (attempt <= 4) {
            return attempt * 1004;
        } else {
            console.log("no more reconnect attempt");
            return -1;
        }
    }
    protected ReconnectAttemptSuccessful(error: ErrorMessage | undefined): void {
        if (error === undefined) {
            console.log("reconnect sussessful");
        } else {
            console.log("reconnect sussessful, but with an error :");
            console.log(error);
        }
    }
}

let lastHeartbeat = Date.now();
let pinging = false; // don't send a ping while we are waiting for a ping answer, in case that answer takes a very long time
const CHECK_INTERVAL = 2000; // test whether the process was put to sleep every 2 seconds
setInterval(async () => {
    const now = Date.now();
    const timePassed = now - lastHeartbeat;
    lastHeartbeat = now;
    const client = window.client as MIVClient | undefined;
    if (client !== undefined && timePassed > CHECK_INTERVAL + 3000 && !pinging) {
        // more than 3 second delta => the process was put to sleep, and now wakes up
        console.log("waking up from sleep, testing the websocket connection...");
        pinging = true;
        const error = await client.EnsureConnectionAsync(true, 128);
        pinging = false;
        if (error === undefined) {
            console.log("websocket working correctly");
        } else {
            console.log("error waking up");
            console.log(error);
        }
    }
}, CHECK_INTERVAL);